/**
 * What language a piece of text is written in, read off the text itself.
 *
 * Used where a message exists and nobody recorded its language: an inbound reply,
 * a draft written before languages were stored, a reply the model drafted. It is
 * NOT used to decide what language to write to somebody in. That is the campaign's
 * setting (see migration 0130), never a guess about a person.
 *
 * The script settles most of it. Arabic script is Urdu when it carries a letter
 * Arabic does not use, and Arabic otherwise. Devanagari is Hindi. Latin text is
 * scored on short everyday words, and only a clear winner is named: a wrong answer
 * shows a person a translation of their own language, so silence (null) is the
 * better failure.
 */

const URDU_ONLY = /[ٹڈڑںےۓھ]/u
const ARABIC_SCRIPT = /[؀-ۿݐ-ݿ]/gu
const DEVANAGARI = /[ऀ-ॿ]/gu
const SCRIPTS: readonly { language: string; test: RegExp }[] = [
  { language: 'ja', test: /[぀-ヿ]/gu },
  { language: 'ko', test: /[가-힯]/gu },
  { language: 'th', test: /[฀-๿]/gu },
  { language: 'ru', test: /[Ѐ-ӿ]/gu },
  { language: 'zh', test: /[一-鿿]/gu },
]

/** Everyday words that are common in one language and rare in the others listed. */
const LATIN_WORDS: Readonly<Record<string, readonly string[]>> = {
  en: ['the', 'and', 'you', 'your', 'is', 'are', 'to', 'of', 'for', 'with', 'this', 'that', 'thanks', 'thank', 'please', 'would', 'what', 'have', 'not', 'it', 'we', 'i'],
  fr: ['le', 'la', 'les', 'des', 'est', 'et', 'vous', 'votre', 'je', 'nous', 'pour', 'avec', 'pas', 'une', 'merci', 'bonjour', 'oui', 'sur', 'dans', 'mais', 'du', 'ça', 'très', 'suis', 'avez'],
  es: ['el', 'los', 'las', 'es', 'y', 'usted', 'para', 'con', 'no', 'una', 'gracias', 'hola', 'sí', 'por', 'pero', 'muy', 'estoy', 'qué', 'como'],
  de: ['der', 'die', 'das', 'und', 'ist', 'sie', 'ich', 'wir', 'für', 'mit', 'nicht', 'eine', 'danke', 'hallo', 'ja', 'auf', 'aber', 'sehr', 'bin', 'haben'],
  pt: ['o', 'os', 'as', 'é', 'e', 'você', 'para', 'com', 'não', 'uma', 'obrigado', 'obrigada', 'olá', 'sim', 'mas', 'muito', 'estou', 'tem'],
  id: ['saya', 'anda', 'dan', 'yang', 'untuk', 'dengan', 'tidak', 'ini', 'itu', 'terima', 'kasih', 'boleh', 'apa', 'ada', 'sudah', 'bisa', 'kami', 'bapak', 'ibu', 'tertarik', 'mau'],
}

function count(text: string, rx: RegExp): number {
  return (text.match(rx) ?? []).length
}

/** The language `text` is written in, as a two-letter code, or null when it cannot tell. */
export function detectTextLanguage(text: string | null | undefined): string | null {
  const t = (text ?? '').trim()
  if (t.length === 0) return null
  const letters = count(t, /\p{L}/gu)
  if (letters === 0) return null

  const arabic = count(t, ARABIC_SCRIPT)
  const devanagari = count(t, DEVANAGARI)
  if (arabic * 2 >= letters) return URDU_ONLY.test(t) ? 'ur' : 'ar'
  if (devanagari * 2 >= letters) return 'hi'
  for (const script of SCRIPTS) {
    if (count(t, script.test) * 2 >= letters) return script.language
  }

  const words = t.toLowerCase().match(/[\p{L}']+/gu) ?? []
  if (words.length === 0) return null
  const scores = Object.entries(LATIN_WORDS).map(([language, list]) => ({
    language,
    score: words.filter((w) => list.includes(w)).length,
  }))
  scores.sort((a, b) => b.score - a.score)
  const [best, second] = scores
  if (best === undefined || best.score === 0) return null
  // A clear winner only. One stray "a" or "no" is not a language.
  if (second !== undefined && best.score < second.score * 2) return null
  if (best.score < 2 && words.length > 3) return null
  return best.language
}
